import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { API_BASE_URL, apiClient } from '../api/client'
import { buildLoginUrl } from './return-to'
import {
  type SessionState,
  getSessionState,
  setAuthenticated,
  setUnauthenticated,
  subscribeSessionState,
} from './session-store'
import type { AuthSession } from './types'

type AuthStatusState =
  | { readonly status: 'loading' }
  | { readonly status: 'unauthenticated' }
  /** Mirrors the `session-store` state of the same name (F0.5): a verified
   * user has a live pre-onboarding session (established by the `409
   * onboarding_required` branch of `POST /auth/login`) but no account yet.
   * `<CreateCompanyPage>` gates on this status rather than reaching into
   * `session-store` directly, matching every other consumer here. */
  | { readonly status: 'onboarding_required' }
  | { readonly status: 'authenticated'; readonly session: AuthSession }

export type AuthContextValue = AuthStatusState & {
  /** Re-probes `GET /auth/session` and updates state from the result. Fails
   * closed: a network error is treated as "unauthenticated", never as
   * "still authenticated" or a thrown error the caller must handle. */
  readonly refresh: () => Promise<void>
  /** Ends the session server-side (`POST /auth/logout`, CSRF-protected) and
   * ALWAYS clears local auth state afterward, even if the network call
   * fails — a stuck "authenticated" UI after the user asked to log out is
   * worse than a session the BFF has to expire on its own on next use. */
  readonly logout: () => Promise<void>
  /** Full-page navigation to the BFF's OIDC login entry (`GET /auth/login`
   * — a 302 to the IdP). Deliberately NOT a `fetch()` call: this is a
   * redirect flow, and the browser must never see the tokens the IdP hands
   * back (C22) — only the BFF callback does. `returnTo`, when given, is
   * validated same-origin-only before being forwarded (see ./return-to). */
  readonly startLogin: (returnTo?: string) => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

function toStatusState(state: SessionState): Exclude<AuthStatusState, { status: 'loading' }> {
  if (state.status === 'authenticated') {
    return { status: 'authenticated', session: state.session }
  }
  if (state.status === 'onboarding_required') {
    return { status: 'onboarding_required' }
  }
  return { status: 'unauthenticated' }
}

/**
 * Provides the app-wide auth session (F0.2). Wrap the app once, near the
 * root, so `useAuth()` is available to the app shell (nav/account display)
 * and to every later auth/onboarding screen (F0.3+) without each of them
 * re-fetching the session.
 *
 * State lives in two places on purpose:
 *  - `lib/auth/session-store.ts` is the single source of truth, readable and
 *    writable from outside React (the authed-fetch middleware invalidates it
 *    directly on a mid-session 401 — see lib/api/auth-middleware.ts).
 *  - This component just mirrors that store into React state so components
 *    re-render on change, plus tracks whether the INITIAL probe has
 *    completed yet (the store itself has no "loading" state — it only knows
 *    authenticated/unauthenticated).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [hasProbedOnce, setHasProbedOnce] = useState(false)
  const [storeState, setStoreState] = useState<SessionState>(() => getSessionState())

  useEffect(() => subscribeSessionState(setStoreState), [])

  const refresh = useCallback(async () => {
    try {
      const { data } = await apiClient.GET('/auth/session')
      if (data) {
        setAuthenticated(data)
      } else {
        // A 401 (no session) surfaces here as a missing `data`, not a thrown
        // error — this is the ordinary "not logged in yet" path.
        setUnauthenticated()
      }
    } catch {
      // Fail closed: a network/parse error must never leave the app
      // reporting a session it couldn't actually verify.
      setUnauthenticated()
    } finally {
      setHasProbedOnce(true)
    }
  }, [])

  useEffect(() => {
    void refresh()
    // Intentionally run once on mount only — `refresh` is stable (empty dep
    // callback) and callers can invoke it again explicitly.
  }, [refresh])

  const logout = useCallback(async () => {
    try {
      // `/auth/logout` is a redirect/operational BFF endpoint intentionally
      // OUTSIDE apps/api/openapi.yaml's contract (see routes.ts's own
      // docstring) — it takes no body and returns 204, so there is no
      // generated type for it. This still goes through the ONE `apiClient`
      // instance (same base URL, same CSRF-attaching middleware) rather than
      // a second hand-rolled fetch; only the path's TS literal type is
      // widened for this one intentionally-uncontracted endpoint.
      await apiClient.POST('/auth/logout' as never, {} as never)
    } catch {
      // Best-effort: the server-side session will simply expire on its own
      // (IDLE/ABSOLUTE TTLs, apps/api/src/modules/bff/session.ts) if this
      // network call never reached the BFF. Local state still clears below
      // either way — see this function's doc comment.
    } finally {
      setUnauthenticated()
    }
  }, [])

  const startLogin = useCallback((returnTo?: string) => {
    window.location.href = buildLoginUrl(API_BASE_URL, returnTo)
  }, [])

  const statusState: AuthStatusState = hasProbedOnce
    ? toStatusState(storeState)
    : { status: 'loading' }

  const value = useMemo<AuthContextValue>(
    () => ({ ...statusState, refresh, logout, startLogin }),
    [statusState, refresh, logout, startLogin],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth() must be used within an <AuthProvider>')
  }
  return context
}
